import * as React from 'react';
import type { ReactNode } from 'react';
import { cx } from '../utils';
import { Eyebrow } from './Eyebrow';
import { Title, type TitleLevel } from './Title';
import type { StyledProps } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';

/* Bounded rest-prop allow-list. NOTE: 'title' is INTENTIONALLY OMITTED
   here because SectionHeader has its own `title` prop (the heading
   ReactNode); allowing the HTML title attribute via rest would shadow
   that prop and confuse the destructure / type surface.

   The literal-key types are picked from React's HTMLAttributes so
   adopters wrapping the component (`interface Props extends
   SectionHeaderProps, React.HTMLAttributes<HTMLDivElement>`) don't
   get `unknown` collisions — the kit defers to React's concrete
   types (codex SectionHeader r1). */
type AllowedRest = Pick<React.HTMLAttributes<HTMLDivElement>, 'id' | 'lang' | 'dir'> & {
  [key: `data-${string}`]: string | number | boolean | undefined;
  [key: `aria-${string}`]: string | boolean | number | undefined;
};
const ALLOWED_LITERAL_KEYS = new Set<string>(['id', 'lang', 'dir']);
function pickAllowed(rest: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(rest)) {
    if (ALLOWED_LITERAL_KEYS.has(key) || key.startsWith('data-') || key.startsWith('aria-')) {
      out[key] = rest[key];
    }
  }
  return out;
}

export interface SectionHeaderProps extends StyledProps, UnsafeStyleProps, AllowedRest {
  kicker?: ReactNode;
  /* `title` is the heading content for the section. Renamed-internally
     vs the HTML `title` attribute — the rest-prop `title` (tooltip)
     would also land here via AllowedRest, so we destructure explicitly
     to avoid collision. */
  title?: ReactNode;
  right?: ReactNode;
  level?: TitleLevel;
}

export const SectionHeader = React.forwardRef<HTMLDivElement, SectionHeaderProps>(
  function SectionHeader(
    { kicker, title, right, level = 'heading', className, unsafe_style, ...rest },
    ref,
  ) {
    const restProps = pickAllowed(
      rest as Record<string, unknown>,
    ) as React.HTMLAttributes<HTMLDivElement>;

    return (
      <div
        ref={ref}
        {...restProps}
        className={cx('j-section-header', className)}
        style={unsafe_style}
      >
        <div className="j-section-header-main">
          {kicker && <Eyebrow tone="brand">{kicker}</Eyebrow>}
          {title && <Title level={level}>{title}</Title>}
        </div>
        {right}
      </div>
    );
  },
);
